export type LauncherPosition =
  | "bottom-left"
  | "bottom-right"
  | "top-left"
  | "top-right";

export type PanelSide = "left" | "right";

export type PublicWidgetConfigResponse = {
  domain: {
    hostname: string;
    id: string;
    requestedHostname: string;
  };
  features: Record<
    string,
    {
      config: unknown;
      enabled: true;
    }
  >;
  project: {
    id: string;
  };
  widget: {
    launcherPosition: LauncherPosition;
    panelSide: PanelSide;
  };
};

export type RuntimeWidgetConfig = PublicWidgetConfigResponse;

const launcherPositions = new Set<LauncherPosition>([
  "bottom-left",
  "bottom-right",
  "top-left",
  "top-right",
]);

const panelSides = new Set<PanelSide>(["left", "right"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasStringField(value: Record<string, unknown>, key: string) {
  return typeof value[key] === "string" && value[key].trim() !== "";
}

export function isPublicWidgetConfigResponse(
  value: unknown,
): value is PublicWidgetConfigResponse {
  if (!isRecord(value)) {
    return false;
  }

  const project = value.project;
  const domain = value.domain;
  const widget = value.widget;
  const features = value.features;

  if (!isRecord(project) || !hasStringField(project, "id")) {
    return false;
  }

  if (
    !isRecord(domain) ||
    !hasStringField(domain, "id") ||
    !hasStringField(domain, "hostname") ||
    !hasStringField(domain, "requestedHostname")
  ) {
    return false;
  }

  if (
    !isRecord(widget) ||
    !launcherPositions.has(widget.launcherPosition as LauncherPosition) ||
    !panelSides.has(widget.panelSide as PanelSide)
  ) {
    return false;
  }

  if (!isRecord(features)) {
    return false;
  }

  return Object.values(features).every((feature) => {
    return isRecord(feature) && feature.enabled === true && "config" in feature;
  });
}

export function toRuntimeWidgetConfig(
  value: PublicWidgetConfigResponse | Record<string, unknown>,
): RuntimeWidgetConfig {
  const features = isRecord(value.features)
    ? Object.fromEntries(
        Object.entries(value.features).filter(([, feature]) => {
          return isRecord(feature) && feature.enabled === true;
        }),
      )
    : {};

  const runtimeValue = {
    ...value,
    features,
  };

  if (!isPublicWidgetConfigResponse(runtimeValue)) {
    throw new Error("Invalid public widget config");
  }

  return runtimeValue;
}
